import React from "react";
import { Typography } from "@mui/material";
import { useSelector, useDispatch } from "react-redux";
import AppState from "../../store/AppState";
import {
  selectIsModalOpen,
  selectModalVariable,
} from "../../store/modal/ModalSelectors";
import { setModalState } from "../../store/modal/ModalActions";
import { updateSelectedAttribute } from "../../store/overlay/OverlayActions";
import { setCurrentCategory } from "../../store/category/CategoryActions";
import { useIntl } from "react-intl";
import CustomDialog from "../common/DialogContainer";
import ButtonComponent from "../common/ButtonComponent";
import { makeStyles, createStyles } from "@material-ui/core";
import {
  getLocksInCategory,
  getPinnedProducts,
  resetLocksInCategory,
  resetPinnedUnPinnedProducts,
  revertProductSequence,
} from "store/product-list/ProductListActions";
import { selectCurrentCatalogId } from "../../store/catalog/CatalogSelectors";
import {
  selectAccountFeatures,
  selectCallerAccountId,
} from "../../store/app-config/AppConfigSelectors";

export const ConfirmCategorySelectionModalId = "ConfirmCategorySelectionModal";

interface Props {
  setSelected: React.Dispatch<React.SetStateAction<string[]>>;
}

const useStyles = makeStyles(() =>
  createStyles({
    dailogAction: {
      width: "100%",
      textAlign: "end",
      margin: "20px 20px 10px",
    },
    divMessage: {
      marginTop: "5px",
    },
  }),
);

const ConfirmCategorySelection: React.FC<Props> = ({ setSelected }: Props) => {
  const dispatch = useDispatch();
  const classes = useStyles();
  const intl = useIntl();
  const categoryId = useSelector((state: AppState) =>
    selectModalVariable(state, ConfirmCategorySelectionModalId, "categoryId"),
  );
  const isSameCategoryReselect = useSelector((state: AppState) =>
    Boolean(
      selectModalVariable(
        state,
        ConfirmCategorySelectionModalId,
        "isSameCategoryReselect",
      ),
    ),
  );
  const isModalOpen = useSelector((state: AppState) =>
    selectIsModalOpen(state, ConfirmCategorySelectionModalId),
  );
  const currentCatalogId = useSelector(selectCurrentCatalogId) ?? "";
  const accountId = useSelector(selectCallerAccountId);
  const features = useSelector((state: AppState) =>
    selectAccountFeatures(state, accountId),
  );
  const isLockedProductsFeatureEnabled = features?.["locked-products"] ?? false;
  const isPinnedProductsFeatureEnabled = features?.["pinned-products"] ?? false;

  const handleClose = () => {
    dispatch(setModalState(ConfirmCategorySelectionModalId, false));
  };

  const handleContinue = () => {
    const targetCategoryId =
      typeof categoryId === "string" ? categoryId : String(categoryId ?? "");

    dispatch(revertProductSequence());
    dispatch(resetLocksInCategory());
    dispatch(resetPinnedUnPinnedProducts());
    dispatch(updateSelectedAttribute(""));
    setSelected([targetCategoryId]);
    dispatch(setCurrentCategory(targetCategoryId));

    // Resets clear server-cached pins/locks. 
    // Re-selecting the same category refetches here.
    if (
      isSameCategoryReselect &&
      targetCategoryId &&
      currentCatalogId
    ) {
      const catalogPayload = {
        catalogId: currentCatalogId,
        categoryId: targetCategoryId,
      };
      if (isPinnedProductsFeatureEnabled) {
        dispatch(getPinnedProducts(catalogPayload));
      }
      if (isLockedProductsFeatureEnabled) {
        dispatch(getLocksInCategory(catalogPayload));
      }
    }

    handleClose();
  };

  const confirmationCategorySelectionTitle = () => {
    return (
      <Typography variant="inherit">
        {" "}
        {intl.formatMessage({
          id: "confirmation.modalTitle",
          defaultMessage: "Warning: Unsaved Changes",
        })}
      </Typography>
    );
  };

  const confirmationCategorySelectionContent = () => {
    return (
      <div className={classes.divMessage}>
        <Typography variant="subtitle1">
          {intl.formatMessage({
            id: "confirmCategorySelection.instruction",
            defaultMessage:
              "You have unsaved changes. Are you sure you want to continue without saving your changes?",
          })}
        </Typography>
      </div>
    );
  };

  const dialogActions = () => {
    return (
      <div className={classes.dailogAction}>
        <ButtonComponent
          color="secondaryButtonColorCTABlue"
          variant="outlined"
          onClick={handleClose}
        >
          {intl.formatMessage({
            id: "general.cancel",
            defaultMessage: "Cancel",
          })}
        </ButtonComponent>
        <ButtonComponent
          color="primary"
          variant="contained"
          onClick={handleContinue}
        >
          {intl.formatMessage({
            id: "confirmCategorySelection.continue",
            defaultMessage: "Continue",
          })}
        </ButtonComponent>
      </div>
    );
  };
  return (
    <CustomDialog
      open={isModalOpen}
      onClose={handleClose}
      title={confirmationCategorySelectionTitle()}
      actions={dialogActions()}
      maxWidth={"md"}
    >
      {confirmationCategorySelectionContent()}
    </CustomDialog>
  );
};

export default React.memo(ConfirmCategorySelection);
